import { Link } from "react-router";
import { IoClose } from "react-icons/io5";
import { MdMeetingRoom, MdContacts, MdDirections } from "react-icons/md";
import { useState, useEffect } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { getRoomsByProjectId } from "../api/locations";






export const MapPopup = ({ projectData, onClose }: { projectData: ProjectInterface, onClose: () => void }) => {
  
  // Calculate project completion based on project items completion percentage
  const getProjectCompletion = (): number => {
    if (!projectData.items || projectData.items.length === 0) {
      // If no items, check project overall status
      if (projectData.status === 'COMPLETED') return 100;
      if (projectData.status === 'IN_PROGRESS') return 50;
      return 0;
    }
    
    // Calculate average completion from all project items
    const totalCompletion = projectData.items.reduce((sum: number, item: any) => {
      return sum + (item.completion_percentage || 0);
    }, 0);
    
    return totalCompletion / projectData.items.length;
  };
  const { keycloak: { token } } = useKeycloak();
  const site = projectData.sites?.[0];
  const [showContacts, setShowContacts] = useState<string | null>(null);
  const [showDirections, setShowDirections] = useState<string | null>(null);
  const [fullRooms, setFullRooms] = useState<RoomInterface[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Load full room data with racks
  useEffect(() => {
    const loadFullRooms = async () => {
      if (!projectData.id || !token) return;
      
      setLoading(true);
      try {
        const roomsData = await getRoomsByProjectId(token, projectData.id);
        setFullRooms(roomsData);
      } catch (error) {
        console.error('Failed to load rooms with racks:', error);
        setFullRooms([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadFullRooms();
  }, [projectData.id, token]);
  
  // Get full address - prioritize Google Address formatted_address
  const getFullAddress = (site: any) => {
    if (!site) return '';
    
    // First, try to use formatted_address from Google Address data
    if (site.google_address_data?.formatted_address) {
      return site.google_address_data.formatted_address;
    }
    
    // Fallback to site.address field
    if (site.address) {
      return site.address;
    }
    
    // Last resort: try to construct from available data
    if (site.google_address_data) {
      const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
      
      // Use new backend fields
      if (city && state) {
        return `${city}, ${state}`;
      }
      if (city) {
        return city;
      }
      
      // Fallback to old fields for backward compatibility  
      if (locality && administrative_area_level_1) {
        return `${locality}, ${administrative_area_level_1}`;
      }
      if (locality) {
        return locality;
      }
    }
    
    return site.name || '';
  };
  
  const displayAddress = getFullAddress(site);
  
  // Count issues by risk level
  const getRiskLevelCounts = () => {
    if (!projectData.items || projectData.items.length === 0) {
      return { high: 0, medium: 0, low: 0, none: 0 };
    }
    
    const counts = { high: 0, medium: 0, low: 0, none: 0 };
    
    projectData.items.forEach(item => {
      const risk = (item.risks || 'none').toLowerCase();
      if (counts.hasOwnProperty(risk)) {
        counts[risk as keyof typeof counts]++;
      } else {
        counts.none++;
      }
    });
    
    return counts;
  };
  
  const riskCounts = getRiskLevelCounts();



  // Use rooms with full data (including racks) if available, otherwise fallback to project rooms
  const allRooms = fullRooms.length > 0 ? fullRooms : (projectData.rooms || []);

  // Helper function to count total racks across all rooms
  const getTotalRacksCount = () => {
    let totalRacks = 0;
    allRooms.forEach(room => {
      if (room.rows) {
        room.rows.forEach(row => {
          if (row.racks) {
            totalRacks += row.racks.length;
          }
        });
      }
    });
    return totalRacks;
  };

  const totalRacks = getTotalRacksCount();

  // Helper function to check if room has contacts data
  const hasContactsData = (room: RoomInterface) => {
    return room.access_contacts || room.access_contacts_name || room.contact_phone || room.contact_email;
  };

  // Helper function to check if room has directions data
  const hasDirectionsData = (room: RoomInterface) => {
    return room.directions || room.access_requirements;
  };

  // Get selected room for modals
  const selectedRoom = allRooms.find(room => room.id === showContacts || room.id === showDirections);

  return (
    <div className="map-popup absolute bg-white w-[400px] z-50 rounded-xl border border-[rgba(112,115,147,0.16)] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-4">
      {/* Header: Logo, Title, Close button */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex gap-3 items-start flex-1 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0 border border-blue-200">
            {/* Generic project icon - no trademarks */}
            <svg 
              className="w-7 h-7 text-blue-600"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className='text-lg font-semibold font-["Arcadia_Text",system-ui,sans-serif] text-gray-900 leading-tight truncate'>
              {projectData.name}
            </h3>
            <p className='font-["Arcadia_Text",system-ui,sans-serif] break-words mt-1' style={{
              fontSize: '13px',
              color: '#6b7280',
              lineHeight: '1.3'
            }}>
              {displayAddress}
            </p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
        >
          <IoClose size={18} />
        </button>
      </div>

      {/* Issues, Racks, and Completion */}
      <div className="flex items-center gap-2 mb-6 ml-[60px]">
        {/* Risk Level Counts */}
        <div className="flex items-center gap-1">
          {riskCounts.high > 0 && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-xs text-red-600 font-medium">{riskCounts.high}</span>
            </div>
          )}
          {riskCounts.medium > 0 && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-yellow-50">
              <div className="w-2 h-2 rounded-full bg-yellow-500" />
              <span className="text-xs text-yellow-600 font-medium">{riskCounts.medium}</span>
            </div>
          )}
          {riskCounts.low > 0 && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-xs text-green-600 font-medium">{riskCounts.low}</span>
            </div>
          )}
          {riskCounts.high === 0 && riskCounts.medium === 0 && riskCounts.low === 0 && (
            <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-50">
              <div className="w-2 h-2 rounded-full bg-gray-500" />
              <span className="text-xs text-gray-600 font-medium">No issues</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-blue-50">
          <div className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-xs text-blue-600 font-medium">
            {loading ? '...' : `${totalRacks} rack${totalRacks !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* Completion Circle */}
        <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-green-50">
          <div className="w-4 h-4 relative">
            <svg className="w-4 h-4 transform -rotate-90" viewBox="0 0 24 24">
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="#e5e7eb"
                strokeWidth="2"
                fill="none"
              />
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="#22c55e"
                strokeWidth="2"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 8}`}
                strokeDashoffset={`${2 * Math.PI * 8 * (1 - (getProjectCompletion() / 100))}`}
                className="transition-all duration-300"
              />
            </svg>
          </div>
          <span className="text-xs text-green-600 font-medium">
            {Math.round(getProjectCompletion())}%
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {allRooms.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <MdMeetingRoom className="mx-auto mb-2 text-gray-300" size={24} />
            <div className="text-sm">
              {loading ? 'Loading rooms...' : 'No rooms available'}
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Rooms ({allRooms.length})
            </div>
            {allRooms.map((room) => (
              <Link 
                key={room.id}
                to={`/projects/${projectData.id}/sites/${room.site_id}/floors/${room.floor_id}/rooms/${room.id}`}
                className="block p-2.5 rounded-lg border border-[rgba(112,115,147,0.16)] hover:bg-[#F0F1F4] transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <MdMeetingRoom className="text-gray-500 flex-shrink-0" size={16} />
                    <div className="flex-1 min-w-0">
                      <div className='text-sm font-medium font-["Arcadia_Text",system-ui,sans-serif] text-gray-700 truncate'>
                        {room.name}
                      </div>
                      <div className='text-xs text-gray-500 font-["Arcadia_Text",system-ui,sans-serif] truncate'>
                        {room.floor?.name || 'Floor'} • {room.rows?.flatMap(row => row.racks || []).length || 0} racks
                      </div>
                    </div>
                  </div>
                
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {hasContactsData(room) && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowContacts(room.id || '');
                        }}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                        title="Contacts"
                      >
                        <MdContacts size={16} />
                      </button>
                    )}
                    
                    {hasDirectionsData(room) && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDirections(room.id || '');
                        }}
                        className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                        title="Directions"
                      >
                        <MdDirections size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </>
        )}
      </div>

      {/* Go to Project Button - Big button at the bottom */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <Link
          to={`/projects/${projectData.id}`}
          className="block w-full text-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm"
          onClick={onClose}
        >
          Go to the project
        </Link>
      </div>

      {/* Contacts Modal */}
      {showContacts && selectedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Room Contacts</h3>
              <button
                onClick={() => setShowContacts(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoClose size={20} />
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700 mb-3">
                {selectedRoom.name}
              </div>
              
              {selectedRoom.access_contacts && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Access Contacts
                  </div>
                  <div className="text-sm text-gray-700">
                    {selectedRoom.access_contacts}
                  </div>
                </div>
              )}
              
              {selectedRoom.access_contacts_name && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Contact Name
                  </div>
                  <div className="text-sm text-gray-700">
                    {selectedRoom.access_contacts_name}
                  </div>
                </div>
              )}
              
              {selectedRoom.contact_phone && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Phone
                  </div>
                  <div className="text-sm text-gray-700">
                    {selectedRoom.contact_phone}
                  </div>
                </div>
              )}
              
              {selectedRoom.contact_email && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Email
                  </div>
                  <div className="text-sm text-gray-700">
                    {selectedRoom.contact_email}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Directions Modal */}
      {showDirections && selectedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Room Directions</h3>
              <button
                onClick={() => setShowDirections(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <IoClose size={20} />
              </button>
            </div>
            
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700 mb-3">
                {selectedRoom.name}
              </div>
              
              {selectedRoom.directions && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Directions
                  </div>
                  <div className="text-sm text-gray-700">
                    {selectedRoom.directions}
                  </div>
                </div>
              )}
              
              {selectedRoom.access_requirements && (
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Access Requirements
                  </div>
                  <div className="text-sm text-gray-700">
                    {selectedRoom.access_requirements}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};